import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { libraryCardsApi, LibraryCard } from "@/lib/api/library-cards";
import { formatPersonName } from "@/lib/person-name";
import { CreditCard, Clock, Package, CheckCircle2, AlertCircle, Wifi } from "lucide-react";

export function LibraryCardDisplay() {
  const [card, setCard] = useState<LibraryCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    loadCard();
  }, []);

  useEffect(() => {
    if (!card?.pickupDeadline || card.status !== "READY") {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const deadline = new Date(card.pickupDeadline!).getTime();
      const difference = deadline - now;

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [card?.pickupDeadline, card?.status]);

  const loadCard = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await libraryCardsApi.getMyCard();
      setCard(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setCard(null);
      } else {
        setError(err.response?.data?.message || "Failed to load library card");
      }
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilDeadline = (deadline?: string) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return <CardSkeleton />;
  }

  if (!card) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">Library Card</p>
            <p className="text-xs text-slate-500">
              Your virtual card will be generated once your email is verified and any required
              payment is completed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(card.status);
  const StatusIcon = statusConfig.icon;
  const awaitingPickup = card.status === "READY";
  const daysUntilDeadline = awaitingPickup
    ? getDaysUntilDeadline(card.pickupDeadline || undefined)
    : null;
  const isExpired =
    card.status === "EXPIRED" || (daysUntilDeadline !== null && daysUntilDeadline < 0);
  const isUrgent =
    awaitingPickup &&
    daysUntilDeadline !== null &&
    daysUntilDeadline >= 0 &&
    daysUntilDeadline <= 3;

  const trackingSteps = getTrackingSteps(card);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* ── Virtual Card ────────────────────────────────── */}
        <div className="md:w-[340px] flex-shrink-0 p-4 sm:p-5">
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-blue-800 rounded-2xl p-5 sm:p-6 text-white shadow-lg shadow-blue-600/25">
            {/* Decorative background shapes */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/[0.07] rounded-full" />
            <div className="absolute top-10 right-6 w-20 h-20 bg-white/[0.05] rounded-full" />
            <div className="absolute -bottom-10 -left-10 w-44 h-44 bg-blue-500/20 rounded-full" />
            <div className="absolute bottom-6 right-20 w-14 h-14 bg-white/[0.03] rounded-full" />

            <div className="relative flex flex-col justify-between min-h-[170px]">
              {/* Top: Brand + Contactless */}
              <div className="flex items-start justify-between">
                <p className="text-[11px] font-bold tracking-[0.25em] text-white/60 uppercase">
                  PhiloLMS
                </p>
                <Wifi className="w-5 h-5 text-white/30 rotate-90" />
              </div>

              {/* Chip */}
              <div className="mt-4 mb-3">
                <div className="w-11 h-[30px] rounded-md overflow-hidden bg-gradient-to-br from-amber-200 to-amber-400 shadow-sm">
                  <div className="w-full h-full grid grid-cols-3 grid-rows-2 gap-px p-[3px]">
                    <div className="bg-amber-300/60 rounded-sm" />
                    <div className="bg-amber-200/40 rounded-sm col-span-2 row-span-2" />
                    <div className="bg-amber-300/60 rounded-sm" />
                  </div>
                </div>
              </div>

              {/* Card Number */}
              <p className="font-mono text-[15px] sm:text-base font-bold tracking-[0.12em] text-white/95 mb-4">
                {card.virtualCardNumber}
              </p>

              {/* Bottom: Holder + Type */}
              <div className="flex items-end justify-between pt-3 border-t border-white/10">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 mb-0.5">
                    Card Holder
                  </p>
                  <p className="text-sm font-medium tracking-wide">
                    {formatPersonName(card.user?.firstName, card.user?.lastName)}
                  </p>
                  <p className="text-[10px] text-white/40 mt-0.5">{card.tenant?.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[8px] font-bold tracking-[0.3em] text-white/30 uppercase">
                    Virtual
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Card Details ────────────────────────────────── */}
        <div className="flex-1 p-4 sm:p-5 md:pl-0 pt-0 md:pt-5">
          {error && (
            <div className="mb-3 p-2.5 rounded-lg bg-red-50 border border-red-100">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center gap-2 mb-3">
            <Badge className={statusConfig.badgeColor}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            {isUrgent && (
              <span className="text-xs font-medium text-amber-600">
                {daysUntilDeadline} {daysUntilDeadline === 1 ? "day" : "days"} left
              </span>
            )}
          </div>

          <p className="text-xs text-slate-500 mb-4">{statusConfig.message}</p>

          {/* Physical Card Tracking — horizontal timeline */}
          {card.status !== "PENDING" && trackingSteps.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                Physical Card
              </p>
              <div className="flex items-start gap-0">
                {trackingSteps.map((step, i) => (
                  <div key={i} className="flex-1 relative">
                    <div className="flex items-center">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          step.completed
                            ? "bg-primary text-white"
                            : step.active
                              ? "bg-primary/10 text-primary ring-2 ring-primary"
                              : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {step.completed ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <span className="text-[10px] font-bold">{i + 1}</span>
                        )}
                      </div>
                      {i < trackingSteps.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 ${step.completed ? "bg-primary" : "bg-slate-200"}`}
                        />
                      )}
                    </div>
                    <div className="mt-1.5 pr-2">
                      <p
                        className={`text-xs font-medium ${step.completed || step.active ? "text-slate-900" : "text-slate-400"}`}
                      >
                        {step.label}
                      </p>
                      {step.date && <p className="text-[10px] text-slate-500">{step.date}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Countdown timer */}
          {card.status === "READY" && timeRemaining && !isExpired && (
            <div className="flex items-center gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100">
              <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-amber-800">Pickup by</span>
                <span className="font-mono text-sm font-bold text-amber-900">
                  {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                  {String(timeRemaining.hours).padStart(2, "0")}:
                  {String(timeRemaining.minutes).padStart(2, "0")}:
                  {String(timeRemaining.seconds).padStart(2, "0")}
                </span>
              </div>
            </div>
          )}

          {isExpired && (
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs font-medium text-red-700">
                Pickup deadline passed. A late fee may apply.
              </p>
            </div>
          )}

          {card.latePickupFine && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-600">Late Pickup Fine</span>
              <span className="text-sm font-semibold text-red-600">
                ₦{card.latePickupFine.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function getStatusConfig(status: LibraryCard["status"]) {
  switch (status) {
    case "PENDING":
      return {
        label: "Pending",
        icon: Clock,
        badgeColor: "bg-slate-100 text-slate-700",
        message: "Your virtual card is ready. Physical card processing will begin soon.",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        icon: Clock,
        badgeColor: "bg-primary/10 text-primary",
        message: "Your physical card is being processed. You'll be notified when it's ready.",
      };
    case "READY":
      return {
        label: "Ready for Pickup",
        icon: Package,
        badgeColor: "bg-emerald-100 text-emerald-700",
        message: "Your physical card is ready! Pick it up before the deadline.",
      };
    case "PICKED_UP":
      return {
        label: "Picked Up",
        icon: CheckCircle2,
        badgeColor: "bg-primary/10 text-primary",
        message: "You've successfully picked up your physical card.",
      };
    case "EXPIRED":
      return {
        label: "Deadline Passed",
        icon: AlertCircle,
        badgeColor: "bg-red-100 text-red-700",
        message: "The pickup deadline has passed. A late fee has been applied.",
      };
    default:
      return {
        label: status,
        icon: Clock,
        badgeColor: "bg-slate-100 text-slate-700",
        message: "",
      };
  }
}

function getTrackingSteps(card: LibraryCard) {
  const formatShort = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      : undefined;

  return [
    {
      label: "Processing",
      date: formatShort(card.processingStartedAt || undefined),
      completed: !!card.processingStartedAt,
      active: card.status === "PROCESSING",
    },
    {
      label: "Ready",
      date: formatShort(card.readyAt || undefined),
      completed: !!card.readyAt,
      active: card.status === "READY",
    },
    {
      label: card.pickedUpAt ? "Picked Up" : "Pickup",
      date: formatShort(card.pickedUpAt || card.pickupDeadline || undefined),
      completed: !!card.pickedUpAt,
      active: false,
    },
  ];
}

function CardSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        <div className="md:w-[340px] flex-shrink-0 p-4 sm:p-5">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl h-[210px] animate-pulse" />
        </div>
        <div className="flex-1 p-4 sm:p-5 md:pl-0 pt-0 md:pt-5 space-y-3">
          <div className="h-5 w-28 bg-slate-100 rounded-full animate-pulse" />
          <div className="h-3 w-48 bg-slate-50 rounded animate-pulse" />
          <div className="pt-2 space-y-2">
            <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
            <div className="flex gap-2 items-center">
              <div className="w-6 h-6 rounded-full bg-slate-100 animate-pulse" />
              <div className="flex-1 h-0.5 bg-slate-100 animate-pulse" />
              <div className="w-6 h-6 rounded-full bg-slate-100 animate-pulse" />
              <div className="flex-1 h-0.5 bg-slate-100 animate-pulse" />
              <div className="w-6 h-6 rounded-full bg-slate-100 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
